import { z } from 'zod/v4';

/** Bump when a material question or answer taxonomy changes. Historical
 * responses keep their version so analytics never silently mixes schemas. */
export const ONBOARDING_QUESTIONNAIRE_VERSION = 3 as const;

export const MANAGEMENT_MODES = [
  'manual',
  'spreadsheets',
  'software',
  'mixed_tools',
  'starting',
] as const;
export type ManagementMode = (typeof MANAGEMENT_MODES)[number];

export const MIGRATION_INTENTS = [
  'start_fresh',
  'import_data',
  'migrate_software',
  'unsure',
  'guided_help',
] as const;
export type MigrationIntent = (typeof MIGRATION_INTENTS)[number];

export const PRIMARY_GOALS = [
  'invoice_faster',
  'stock_accuracy',
  'manage_purchases',
  'track_payments',
  'run_pos',
  'sell_online',
  'manage_team_stores',
  'centralize_operations',
] as const;
export type PrimaryGoal = (typeof PRIMARY_GOALS)[number];

const PRIMARY_GOAL_MODULES: Record<PrimaryGoal, readonly string[]> = {
  invoice_faster: ['sales'],
  stock_accuracy: ['stock'],
  manage_purchases: ['purchases'],
  track_payments: ['payments', 'treasury'],
  run_pos: ['caisse'],
  sell_online: ['storefront'],
  manage_team_stores: ['stock', 'caisse'],
  centralize_operations: ['sales', 'purchases', 'stock', 'payments'],
};

export function recommendedModulesForPrimaryGoal(
  goal: PrimaryGoal | null,
): readonly string[] {
  return goal ? PRIMARY_GOAL_MODULES[goal] : [];
}

/** Merge recommendations for a multi-select needs answer without returning
 * duplicate modules when several needs point to the same capability. */
export function recommendedModulesForPrimaryGoals(
  goals: readonly PrimaryGoal[],
): readonly string[] {
  return [...new Set(goals.flatMap((goal) => PRIMARY_GOAL_MODULES[goal]))];
}

export const ONBOARDING_SALES_CHANNELS = [
  'store',
  'online',
  'b2b',
  'field',
] as const;
export type OnboardingSalesChannel = (typeof ONBOARDING_SALES_CHANNELS)[number];

export const ONBOARDING_PAYMENT_MODES = [
  'cash',
  'checks',
  'transfer',
  'installments',
] as const;
export type OnboardingPaymentMode = (typeof ONBOARDING_PAYMENT_MODES)[number];

/** These explain the high-level `online` sales channel without conflating an
 * owned website with social, marketplace or messaging sales. */
export const ONLINE_SALES_CHANNELS = [
  'own_website',
  'social_media',
  'marketplace',
  'messaging',
  'wants_to_start',
] as const;
export type OnlineSalesChannel = (typeof ONLINE_SALES_CHANNELS)[number];

export const ECOMMERCE_PLATFORMS = [
  'shopify',
  'woocommerce',
  'prestashop',
  'custom',
  'other',
  'unknown',
] as const;
export type EcommercePlatform = (typeof ECOMMERCE_PLATFORMS)[number];

export const ACQUISITION_SOURCES = [
  'recommendation',
  'facebook_instagram',
  'google_search',
  'tiktok',
  'youtube',
  'event_training',
  'gesty_team',
  'other',
] as const;
export type AcquisitionSource = (typeof ACQUISITION_SOURCES)[number];

export interface OnboardingProfileInput {
  questionnaireVersion: typeof ONBOARDING_QUESTIONNAIRE_VERSION;
  managementMode: ManagementMode;
  previousSoftwareName: string | null;
  migrationIntent: MigrationIntent;
  primaryGoals: PrimaryGoal[];
  activitySector: string | null;
  salesChannels: OnboardingSalesChannel[];
  paymentModes: OnboardingPaymentMode[];
  onlineSalesChannels: OnlineSalesChannel[];
  websiteUrl: string | null;
  ecommercePlatform: EcommercePlatform | null;
  ecommercePlatformName: string | null;
  acquisitionSource: AcquisitionSource;
  acquisitionSourceDetail: string | null;
  assistanceRequested: boolean;
  contactConsent: boolean;
}

/** Read model is deliberately tolerant of future answer codes. A stored row must
 * remain visible in the admin console before the UI learns its labels. */
export interface OnboardingProfileRecord {
  questionnaireVersion: number;
  managementMode: string;
  previousSoftwareName: string | null;
  migrationIntent: string;
  primaryGoals: string[];
  activitySector: string | null;
  salesChannels: string[];
  paymentModes: string[];
  onlineSalesChannels: string[];
  websiteUrl: string | null;
  ecommercePlatform: string | null;
  ecommercePlatformName: string | null;
  acquisitionSource: string;
  acquisitionSourceDetail: string | null;
  assistanceRequested: boolean;
  contactConsent: boolean;
  tenantId: string;
  createdBy: string | null;
  capturedAt: string;
}

/** Turn a user-entered hostname into an absolute web URL before validation.
 * Only HTTP(S) is accepted; embedded credentials are rejected because this is
 * an admin-visible storefront link, never a secret. */
export function normalizeOnboardingWebsiteUrl(raw: string): string | null {
  const trimmed = raw.trim();
  if (!trimmed) return null;
  const candidate = /^[a-z][a-z\d+.-]*:\/\//i.test(trimmed)
    ? trimmed
    : `https://${trimmed}`;
  try {
    const parsed = new URL(candidate);
    if (!['http:', 'https:'].includes(parsed.protocol)) return null;
    if (parsed.username || parsed.password || !parsed.hostname) return null;
    return parsed.toString();
  } catch {
    return null;
  }
}

const optionalTrimmedText = (max: number) =>
  z.union([z.string().trim().min(1).max(max), z.null()]);

const onboardingWebsiteUrlSchema = z
  .string()
  .trim()
  .url()
  .max(2_048)
  .refine((value) => {
    try {
      const parsed = new URL(value);
      return (
        ['http:', 'https:'].includes(parsed.protocol) &&
        !parsed.username &&
        !parsed.password
      );
    } catch {
      return false;
    }
  }, 'unsupported_website_url');

const uniqueEnumArray = <T extends readonly [string, ...string[]]>(values: T) =>
  z
    .array(z.enum(values))
    .max(values.length)
    .refine((items) => new Set(items).size === items.length, {
      message: 'duplicate_answers',
    });

/** Runtime boundary for the create-tenant mutation. SQL repeats the critical
 * allow-lists and conditional checks because browser validation is usability,
 * not data integrity. */
export const onboardingProfileSchema: z.ZodType<OnboardingProfileInput> = z
  .object({
    questionnaireVersion: z.literal(ONBOARDING_QUESTIONNAIRE_VERSION),
    managementMode: z.enum(MANAGEMENT_MODES),
    previousSoftwareName: optionalTrimmedText(120),
    migrationIntent: z.enum(MIGRATION_INTENTS),
    primaryGoals: uniqueEnumArray(PRIMARY_GOALS).min(1),
    activitySector: optionalTrimmedText(120),
    salesChannels: uniqueEnumArray(ONBOARDING_SALES_CHANNELS),
    paymentModes: uniqueEnumArray(ONBOARDING_PAYMENT_MODES),
    onlineSalesChannels: uniqueEnumArray(ONLINE_SALES_CHANNELS),
    websiteUrl: z.union([onboardingWebsiteUrlSchema, z.null()]),
    ecommercePlatform: z.union([z.enum(ECOMMERCE_PLATFORMS), z.null()]),
    ecommercePlatformName: optionalTrimmedText(120),
    acquisitionSource: z.enum(ACQUISITION_SOURCES),
    acquisitionSourceDetail: optionalTrimmedText(120),
    assistanceRequested: z.boolean(),
    contactConsent: z.boolean(),
  })
  .superRefine((profile, context) => {
    const alreadyUsesSoftware =
      profile.managementMode === 'software' ||
      profile.managementMode === 'mixed_tools';
    if (alreadyUsesSoftware && !profile.previousSoftwareName) {
      context.addIssue({
        code: 'custom',
        path: ['previousSoftwareName'],
        message: 'previous_software_required',
      });
    }
    if (!alreadyUsesSoftware && profile.previousSoftwareName) {
      context.addIssue({
        code: 'custom',
        path: ['previousSoftwareName'],
        message: 'previous_software_not_applicable',
      });
    }

    const sellsOnline = profile.salesChannels.includes('online');
    if (sellsOnline && profile.onlineSalesChannels.length === 0) {
      context.addIssue({
        code: 'custom',
        path: ['onlineSalesChannels'],
        message: 'online_sales_channel_required',
      });
    }
    if (
      profile.onlineSalesChannels.includes('wants_to_start') &&
      profile.onlineSalesChannels.length > 1
    ) {
      context.addIssue({
        code: 'custom',
        path: ['onlineSalesChannels'],
        message: 'online_starting_must_be_exclusive',
      });
    }
    if (
      !sellsOnline &&
      (profile.onlineSalesChannels.length > 0 ||
        profile.websiteUrl !== null ||
        profile.ecommercePlatform !== null)
    ) {
      context.addIssue({
        code: 'custom',
        path: ['salesChannels'],
        message: 'online_channel_required',
      });
    }
    if (
      profile.onlineSalesChannels.includes('own_website') &&
      profile.websiteUrl === null
    ) {
      context.addIssue({
        code: 'custom',
        path: ['websiteUrl'],
        message: 'website_url_required',
      });
    }
    const ownsWebsite = profile.onlineSalesChannels.includes('own_website');
    if (!ownsWebsite && profile.websiteUrl !== null) {
      context.addIssue({
        code: 'custom',
        path: ['websiteUrl'],
        message: 'owned_website_required',
      });
    }
    if (ownsWebsite && profile.ecommercePlatform === null) {
      context.addIssue({
        code: 'custom',
        path: ['ecommercePlatform'],
        message: 'ecommerce_platform_required',
      });
    }
    if (!ownsWebsite && profile.ecommercePlatform !== null) {
      context.addIssue({
        code: 'custom',
        path: ['ecommercePlatform'],
        message: 'owned_website_required',
      });
    }
    if (
      profile.ecommercePlatform === 'other' &&
      !profile.ecommercePlatformName
    ) {
      context.addIssue({
        code: 'custom',
        path: ['ecommercePlatformName'],
        message: 'ecommerce_platform_name_required',
      });
    }
    if (
      profile.ecommercePlatform !== 'other' &&
      profile.ecommercePlatformName
    ) {
      context.addIssue({
        code: 'custom',
        path: ['ecommercePlatformName'],
        message: 'ecommerce_platform_name_not_applicable',
      });
    }
    if (
      profile.acquisitionSource === 'other' &&
      !profile.acquisitionSourceDetail
    ) {
      context.addIssue({
        code: 'custom',
        path: ['acquisitionSourceDetail'],
        message: 'acquisition_source_detail_required',
      });
    }
    if (
      profile.acquisitionSource !== 'other' &&
      profile.acquisitionSourceDetail
    ) {
      context.addIssue({
        code: 'custom',
        path: ['acquisitionSourceDetail'],
        message: 'acquisition_source_detail_not_applicable',
      });
    }
    if (profile.assistanceRequested && !profile.contactConsent) {
      context.addIssue({
        code: 'custom',
        path: ['contactConsent'],
        message: 'contact_consent_required',
      });
    }
  });

/** Tolerant transport validation for versioned admin read rows. Current writes
 * use the strict schema above; historical or future codes render with a raw
 * code fallback instead of breaking the entire dashboard query. */
export const storedOnboardingProfileSchema = z.object({
  questionnaireVersion: z.number().int().positive(),
  managementMode: z.string().trim().min(1).max(80),
  previousSoftwareName: optionalTrimmedText(120),
  migrationIntent: z.string().trim().min(1).max(80),
  primaryGoals: z.array(z.string().trim().min(1).max(80)).min(1).max(32),
  activitySector: optionalTrimmedText(120),
  salesChannels: z.array(z.string().trim().min(1).max(80)).max(32),
  paymentModes: z.array(z.string().trim().min(1).max(80)).max(32),
  onlineSalesChannels: z.array(z.string().trim().min(1).max(80)).max(32),
  websiteUrl: z.union([onboardingWebsiteUrlSchema, z.null()]),
  ecommercePlatform: optionalTrimmedText(80),
  ecommercePlatformName: optionalTrimmedText(120),
  acquisitionSource: z.string().trim().min(1).max(80),
  acquisitionSourceDetail: optionalTrimmedText(120),
  assistanceRequested: z.boolean(),
  contactConsent: z.boolean(),
  tenantId: z.string().uuid(),
  createdBy: z.union([z.string().uuid(), z.null()]),
  capturedAt: z.string().min(1),
});

export interface OnboardingBreakdown<K extends string> {
  key: K;
  count: number;
  percentage: number;
}

export interface OnboardingStatistics {
  totalTenants: number;
  capturedProfiles: number;
  missingProfiles: number;
  coverageRate: number;
  softwareAdoptionRate: number;
  onlineSalesRate: number;
  websiteCoverageRate: number;
  assistanceRequests: number;
  softwareMigrationCandidates: number;
  managementModes: OnboardingBreakdown<ManagementMode>[];
  primaryGoals: OnboardingBreakdown<PrimaryGoal>[];
  migrationIntents: OnboardingBreakdown<MigrationIntent>[];
  onlineChannels: OnboardingBreakdown<OnlineSalesChannel>[];
  ecommercePlatforms: OnboardingBreakdown<EcommercePlatform>[];
  acquisitionSources: OnboardingBreakdown<AcquisitionSource>[];
  topSoftware: Array<{ name: string; count: number }>;
  topOtherEcommercePlatforms: Array<{ name: string; count: number }>;
  topOtherAcquisitionSources: Array<{ name: string; count: number }>;
}

function percentage(part: number, whole: number): number {
  return whole > 0 ? (part / whole) * 100 : 0;
}

function buildBreakdown<K extends string>(
  keys: readonly K[],
  values: readonly string[],
  denominator = values.length,
): OnboardingBreakdown<K>[] {
  return keys.map((key) => {
    const count = values.filter((value) => value === key).length;
    return { key, count, percentage: percentage(count, denominator) };
  });
}

/** Multi-select answers are respondent rates, not a share of all clicks. */
function buildMultiSelectBreakdown<K extends string>(
  keys: readonly K[],
  answersByRespondent: readonly (readonly string[])[],
): OnboardingBreakdown<K>[] {
  return keys.map((key) => {
    const count = answersByRespondent.filter((answers) =>
      answers.includes(key),
    ).length;
    return {
      key,
      count,
      percentage: percentage(count, answersByRespondent.length),
    };
  });
}

/** Legacy tenants are counted as missing, never guessed into a category. */
export function buildOnboardingStatistics(
  profiles: readonly OnboardingProfileRecord[],
  totalTenants: number,
): OnboardingStatistics {
  const capturedProfiles = profiles.length;
  const softwareUsers = profiles.filter(
    ({ managementMode }) =>
      managementMode === 'software' || managementMode === 'mixed_tools',
  );
  const onlineRespondents = profiles.filter(({ salesChannels }) =>
    salesChannels.includes('online'),
  );
  const onlineSellers = onlineRespondents.filter(
    ({ onlineSalesChannels }) =>
      onlineSalesChannels.length > 0 &&
      !onlineSalesChannels.includes('wants_to_start'),
  );
  const sellersWithWebsite = onlineSellers.filter(
    ({ websiteUrl }) => websiteUrl !== null,
  );
  const websiteOwners = onlineSellers.filter(({ onlineSalesChannels }) =>
    onlineSalesChannels.includes('own_website'),
  );
  const assistanceRequests = profiles.filter(
    ({ assistanceRequested, contactConsent }) =>
      assistanceRequested && contactConsent,
  ).length;

  const countNames = (
    names: ReadonlyArray<string | null | undefined>,
  ): Array<{ name: string; count: number }> => {
    const counts = new Map<string, { name: string; count: number }>();
    for (const rawName of names) {
      const name = rawName?.trim();
      if (!name) continue;
      const normalized = name.toLocaleLowerCase('fr-TN');
      const current = counts.get(normalized);
      counts.set(normalized, {
        name: current?.name ?? name,
        count: (current?.count ?? 0) + 1,
      });
    }
    return [...counts.values()].sort(
      (left, right) =>
        right.count - left.count || left.name.localeCompare(right.name),
    );
  };

  const topSoftware = countNames(
    softwareUsers.map(({ previousSoftwareName }) => previousSoftwareName),
  );
  const topOtherEcommercePlatforms = countNames(
    websiteOwners
      .filter(({ ecommercePlatform }) => ecommercePlatform === 'other')
      .map(({ ecommercePlatformName }) => ecommercePlatformName),
  );
  const topOtherAcquisitionSources = countNames(
    profiles
      .filter(({ acquisitionSource }) => acquisitionSource === 'other')
      .map(({ acquisitionSourceDetail }) => acquisitionSourceDetail),
  );

  return {
    totalTenants,
    capturedProfiles,
    missingProfiles: Math.max(0, totalTenants - capturedProfiles),
    coverageRate: percentage(capturedProfiles, totalTenants),
    softwareAdoptionRate: percentage(softwareUsers.length, capturedProfiles),
    onlineSalesRate: percentage(onlineSellers.length, capturedProfiles),
    websiteCoverageRate: percentage(
      sellersWithWebsite.length,
      onlineSellers.length,
    ),
    assistanceRequests,
    softwareMigrationCandidates: profiles.filter(
      ({ migrationIntent }) => migrationIntent === 'migrate_software',
    ).length,
    managementModes: buildBreakdown(
      MANAGEMENT_MODES,
      profiles.map(({ managementMode }) => managementMode),
    ),
    primaryGoals: buildMultiSelectBreakdown(
      PRIMARY_GOALS,
      profiles.map(({ primaryGoals }) => primaryGoals),
    ),
    migrationIntents: buildBreakdown(
      MIGRATION_INTENTS,
      profiles.map(({ migrationIntent }) => migrationIntent),
    ),
    onlineChannels: buildMultiSelectBreakdown(
      ONLINE_SALES_CHANNELS,
      onlineRespondents.map(({ onlineSalesChannels }) => onlineSalesChannels),
    ),
    ecommercePlatforms: buildBreakdown(
      ECOMMERCE_PLATFORMS,
      websiteOwners.flatMap(({ ecommercePlatform }) =>
        ecommercePlatform ? [ecommercePlatform] : [],
      ),
      websiteOwners.length,
    ),
    acquisitionSources: buildBreakdown(
      ACQUISITION_SOURCES,
      profiles.map(({ acquisitionSource }) => acquisitionSource),
    ),
    topSoftware,
    topOtherEcommercePlatforms,
    topOtherAcquisitionSources,
  };
}

// Jeu de données de découverte semé au premier login (voir demo-dataset.ts).
export * from './demo-dataset';
